import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Franchise & Retail Outlet Setup", item: "https://cashmyproperty.ai/services/franchise" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Which brands can set up on my land?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We hold direct relationships with Shell, BP, HP, Jio-bp (fuel stations), Tata Power, Statiq (EV charging), McDonald's, Starbucks, KFC, Burger King, Domino's (QSR), and Big Bazaar, D-Mart, Reliance Smart (retail). Our brand network covers 50+ national and international brands actively expanding across India.",
      },
    },
    {
      "@type": "Question",
      name: "How soon can a franchise or retail outlet be operational on my land?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Brand lease signing typically takes 60–90 days from initial connection. Construction takes 3–6 months depending on the format (fuel stations and QSRs are fastest). You can expect rent to begin within 6–9 months of starting the process with us.",
      },
    },
    {
      "@type": "Question",
      name: "How does franchise placement increase my property value?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Having a branded fuel station, QSR, or retail outlet on your land immediately increases its commercial visibility and footfall. We typically see 15–25% property value appreciation within the first year of operation. Adjacent plots also become more valuable, creating a compounding effect.",
      },
    },
  ],
};

const steps = [
  { step: "01", title: "Location & Visibility Scoring", desc: "AI evaluates your plot's highway frontage, traffic count, visibility from the road, approach distance, and catchment demographics. High-scoring plots get priority brand interest.", icon: "ri-eye-line" },
  { step: "02", title: "Brand Matchmaking", desc: "Based on your plot's profile, we present it to our brand network. Fuel companies want highway frontage. QSRs want corner plots. Retail chains want neighborhood density. We know who wants what.", icon: "ri-team-line" },
  { step: "03", title: "Site Visit & Brand Approval", desc: "Shortlisted brands conduct a site survey — traffic count verification, soil testing, and commercial viability assessment. We coordinate everything while keeping you informed.", icon: "ri-map-pin-line" },
  { step: "04", title: "Lease Agreement & Registration", desc: "Our legal team negotiates rent rates, escalation, and lock-in periods. Brand lease deeds are registered with full compliance. Rent terms are guaranteed and enforceable.", icon: "ri-file-text-line" },
  { step: "05", title: "Construction & Rent Starts", desc: "The brand constructs and outfits their outlet. You start receiving monthly rent from Day 1 of operations. We provide ongoing liaison for the entire lease duration.", icon: "ri-store-3-line" },
];

const brands = [
  { category: "Fuel Stations", brands: ["Shell", "BP", "HP", "Jio-bp", "Nayara"], icon: "ri-oil-line", avgRent: "₹25–60/sq.ft/month" },
  { category: "EV Charging", brands: ["Tata Power", "Statiq", "Ather Grid", "Zeon", "ChargeZone"], icon: "ri-plug-line", avgRent: "₹15–35/sq.ft/month" },
  { category: "QSR & Drive-Thru", brands: ["McDonald's", "Starbucks", "KFC", "Burger King", "Domino's"], icon: "ri-restaurant-line", avgRent: "₹30–80/sq.ft/month" },
  { category: "Retail & Supermarkets", brands: ["D-Mart", "Reliance Smart", "Big Bazaar", "Vishal Mega Mart"], icon: "ri-shopping-basket-line", avgRent: "₹20–50/sq.ft/month" },
];

const benefits = [
  { icon: "ri-star-line", title: "Premium Brand Association", desc: "A Shell or McDonald's on your land instantly elevates its commercial profile. Adjacent land values rise, and other brands become interested." },
  { icon: "ri-calendar-check-line", title: "Fast Time-to-Income", desc: "Lease signed in 60–90 days. Construction in 3–6 months. Rent flowing within 6–9 months from start — faster than any other monetization strategy." },
  { icon: "ri-line-chart-line", title: "Property Value Jump", desc: "Land with a branded commercial tenant commands 15–25% higher valuation. You build equity while earning rent." },
  { icon: "ri-shield-check-line", title: "Zero Investment From You", desc: "The brand designs, funds, and builds the outlet entirely at their cost. You provide land, they build their business on it." },
  { icon: "ri-git-repository-private-line", title: "Multi-Brand Potential", desc: "Large highway plots can host multiple brands — a fuel station, QSR, and EV hub on the same land, multiplying your rental income." },
  { icon: "ri-rotate-lock-line", title: "Long-Term Security", desc: "Brand leases typically run 15–20 years with built-in rent escalation. Major brands don't default — they invest too much in their outlets." },
];

const faqs = [
  { q: "Which brands can set up on my land?", a: "We hold direct relationships with Shell, BP, HP, Jio-bp (fuel stations), Tata Power, Statiq (EV charging), McDonald's, Starbucks, KFC, Burger King, Domino's (QSR), and Big Bazaar, D-Mart, Reliance Smart (retail). Our brand network covers 50+ national and international brands actively expanding across India." },
  { q: "How soon can a franchise or retail outlet be operational on my land?", a: "Brand lease signing typically takes 60–90 days from initial connection. Construction takes 3–6 months depending on the format (fuel stations and QSRs are fastest). You can expect rent to begin within 6–9 months of starting the process with us." },
  { q: "How does franchise placement increase my property value?", a: "Having a branded fuel station, QSR, or retail outlet on your land immediately increases its commercial visibility and footfall. We typically see 15–25% property value appreciation within the first year of operation. Adjacent plots also become more valuable, creating a compounding effect." },
];

export default function FranchiseServicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Franchise & Retail Outlet Setup — Place Brands on Your Highway Land | CashMyProperty.ai",
    description: "Turn your highway or roadside plot into a branded commercial outlet. Partner with Shell, McDonald's, Starbucks, Tata Power and 50+ brands. Zero investment — the brand builds and pays you monthly rent. Lease signed in 60–90 days, property value rises 15–25%.",
    canonicalUrl: "https://cashmyproperty.ai/services/franchise",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20fuel%20station%20and%20QSR%20drive-thru%20complex%20at%20dusk%2C%20glowing%20brand%20signage%20against%20dramatic%20twilight%20sky%2C%20busy%20highway%20with%20light%20trails%2C%20multiple%20brand%20outlets%20visible%2C%20warm%20amber%20and%20teal%20neon%20lighting%2C%20cinematic%20commercial%20photography%2C%20India%20highway%20retail%20development%2C%20ultra%20detailed&width=1800&height=800&seq=service-detail-franchise-hero&orientation=landscape"
          alt="Franchise & Retail Outlet Setup — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Franchise &</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Retail Outlet Setup</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Place Shell, McDonald's, Starbucks, or Tata Power on your highway land. They build, you earn — rent starts in 6 months.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Franchise & Retail Outlet Setup</span>
          </nav>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What is Franchise Placement?</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Highway Land = Brand Goldmine
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                If your land faces a highway, national highway, or state highway, you are sitting on a goldmine that major brands are desperate to access. Fuel companies, QSR chains, and retail giants spend crores scouting for exactly your type of plot.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Through our brand network of <strong className="text-foreground-950">50+ national and international brands</strong>, we connect your roadside land with companies that will build their outlet at their own cost and pay you premium monthly rent — typically <strong className="text-foreground-950">₹25–80 per sq.ft/month</strong> for prime locations.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                The best part? A branded commercial tenant on your land increases its <strong className="text-foreground-950">market value by 15–25%</strong> almost immediately. You earn rent AND build equity — the ultimate win-win.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20Shell%20fuel%20station%20with%20integrated%20Starbucks%20drive-thru%20at%20night%2C%20illuminated%20brand%20signage%2C%20cars%20refueling%2C%20dramatic%20dark%20moody%20atmosphere%2C%20neon%20reflections%20on%20wet%20pavement%2C%20cinematic%20commercial%20photography%2C%20deep%20teal%20and%20amber%20lighting%2C%20India%20highway%20retail&width=800&height=600&seq=franchise-fuel-station-01&orientation=landscape"
                alt="Franchise Outlet"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brand Network */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Our Brand Network</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">50+ Brands Actively Seeking Land</h2>
            <p className="text-foreground-600">These are not cold leads — these are brands we have active relationships with.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {brands.map((cat) => (
              <div key={cat.category} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300 h-full">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${cat.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-3">{cat.category}</h3>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {cat.brands.map((b) => (
                    <span key={b} className="text-xs px-2 py-1 rounded-md bg-primary-500/10 text-primary-700 font-medium">{b}</span>
                  ))}
                </div>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">{cat.avgRent}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">How Brand Placement Works</h2>
            <p className="text-foreground-600">From highway plot to branded outlet. Rent starts in as little as 6 months.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Roadside Landowners Choose Franchise Placement</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "50+", label: "Brand Relationships" },
              { value: "60–90 Days", label: "Lease Signing Time" },
              { value: "15–25%", label: "Property Value Jump" },
              { value: "₹25–80", label: "Rent per Sq.Ft/Month" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Is Your Roadside Land Brand-Ready?
          </h2>
          <p className="text-foreground-600 mb-8">
            Our AI evaluates your plot's traffic count, visibility, and catchment area — then matches it with the brands most likely to say yes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Brand Match Report — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}